import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import {
  CloseIcon,
  PlusDualIcon,
  SearchIcon,
  TwoChatBubbleIcon,
} from "components";
import { memo } from "react";
import type { ChatMember } from "shared/types/chatMembers";

interface ThreadHeaderProps {
  members: ChatMember[];
  onMembersClick: () => void;
  onSearchClick: () => void;
  onClose: () => void;
}

export const ThreadHeader = memo(function ThreadHeader({
  members,
  onMembersClick,
  onSearchClick,
  onClose,
}: ThreadHeaderProps) {
  const theme = useTheme();

  const getMemberNames = (): string => {
    if (members.length === 0) return "";
    const names = members.map((m) => m.display_name || "Unknown");
    if (names.length <= 2) return names.join(", ");
    return `${names[0]}, ${names[1]} +${names.length - 2}`;
  };

  return (
    <Stack
      px="24px"
      py="16px"
      sx={{
        backgroundColor: theme.palette.neutral.main,
        flexShrink: 0,
        boxShadow: "0px 0px 4px 0px #0000000F,0px 0px 2px 0px #0000000A",
      }}
      direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      <Box
        component="button"
        onClick={onMembersClick}
        sx={{
          bgcolor: "transparent",
          cursor: "pointer",
          border: "1px solid transparent",
          p: "2px 4px",
          borderRadius: "8px",
          ":hover": {
            backgroundColor: theme.palette.neutral["005"],
            border: `1px solid ${theme.palette.neutral["010"]}`,
          },
        }}
      >
        <Stack direction="row" gap="8px" alignItems="center">
          <Stack
            sx={{
              width: "32px",
              height: "32px",
              backgroundColor: "#FFF",
              border: `1.5px solid ${theme.palette.neutral["010"]}`,
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "100%",
              position: "relative",
            }}
          >
            <TwoChatBubbleIcon
              sx={{ width: "20px", height: "20px" }}
              htmlColor={theme.palette.lightBg.high}
            />
            <PlusDualIcon
              htmlColor={theme.palette.primary.main}
              sx={{
                position: "absolute",
                bottom: "-4px",
                right: "-4px",
                width: "16px",
                height: "16px",
                borderRadius: "50%",
              }}
            />
          </Stack>

          <Typography
            maxWidth="400px"
            variant="subtitle1"
            noWrap
            textOverflow="ellipsis"
            sx={{ textTransform: "capitalize" }}
          >
            {getMemberNames()}
          </Typography>
        </Stack>
      </Box>

      <Stack direction="row" gap="12px" alignItems="center">
        <IconButton
          aria-label="Search in thread"
          onClick={onSearchClick}
          sx={{
            padding: "0px",
            ":hover": { backgroundColor: "transparent" },
          }}
        >
          <SearchIcon htmlColor={theme.palette.lightBg.high} />
        </IconButton>
        <IconButton
          aria-label="Close thread panel"
          onClick={onClose}
          sx={{
            padding: "0px",
            ":hover": { backgroundColor: "transparent" },
          }}
        >
          <CloseIcon htmlColor={theme.palette.lightBg.high} />
        </IconButton>
      </Stack>
    </Stack>
  );
});
